import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Form,
  Link,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Strong,
} from '@customerio/pluma-components/react';
import { useSignupFlow } from '../signup-flow';
import { SignupAnalyticsEvent, signupTypeProps } from '../utils/analytics';
import { useInboxLink } from './use-inbox-link';
import { useVerificationSubmit } from './use-verification-submit';
import { validateCodeInput } from './verification-code';
import { VerificationCodeInput } from './verification-code-input';

export function ConfirmationStep() {
  const { formValues, patchFormValues, effectiveSignupType, isGoogleSignup, trackSignupEvent } = useSignupFlow();
  const { submitCode, resendCode, isSubmitting, hasSubmitErrored } = useVerificationSubmit();
  const inbox = useInboxLink();

  const { email, signupVerificationCode: code } = formValues;

  // The button ships enabled on an untouched field, and only starts reflecting
  // validation once something has been typed or a submit attempted.
  const [hasValidated, setHasValidated] = useState(false);
  const validationError = validateCodeInput(code);

  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    trackSignupEvent(
      SignupAnalyticsEvent.VerifyEmailViewed,
      signupTypeProps({ signupType: effectiveSignupType, isGoogleSignup }),
    );
  }, [trackSignupEvent, effectiveSignupType, isGoogleSignup]);

  // Submit as soon as a complete code is entered so the common case needs no
  // click — but stop once a submit has failed, so a visitor correcting a code
  // isn't fighting a request on every keystroke.
  const handleCodeChange = (next: string) => {
    setHasValidated(true);
    patchFormValues({ signupVerificationCode: next });
    if (validateCodeInput(next) == null && !hasSubmitErrored) {
      void submitCode(next);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setHasValidated(true);
    void submitCode(code);
  };

  return (
    <Box data-testid="signup-step-confirmation">
      <PanelHeader>
        <PanelTitle level="1" data-test-signup-form-heading>
          Verify your email
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Form onSubmit={handleSubmit}>
          <Paragraph variant="secondary" mb="200" data-test-signup-form-subheading>
            We&apos;ve sent a verification code to <Strong>{email}.</Strong> Enter it below.{' '}
            {inbox.isLoading ? (
              <Link
                variant="secondary"
                onClick={(event) => event.preventDefault()}
                data-test-signup-inbox-link-placeholder
              >
                Open inbox…
              </Link>
            ) : null}
          </Paragraph>

          {inbox.link != null ? (
            <Button
              variant="secondary"
              iconSrc={inbox.link.logoUrl}
              w="full"
              mb="300"
              aria-label={`Open ${inbox.link.label}`}
              onClick={inbox.openInbox}
              data-test-signup-inbox-link
            >
              Open in {inbox.link.label}
            </Button>
          ) : null}

          <VerificationCodeInput
            value={code}
            onValueChange={handleCodeChange}
            error={hasSubmitErrored ? validationError : null}
          />

          <Paragraph variant="secondary" sizeVariant="xs" mb="200">
            Has your code expired? Don&apos;t see the email? Check your spam or{' '}
            <Link variant="secondary" onClick={() => void resendCode()} data-test-signup-resend-code>
              request a new verification code.
            </Link>
          </Paragraph>

          <Flex justifyContent="flex-end" mt="200">
            <Button
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              isDisabled={hasValidated && validationError != null}
              isLoading={isSubmitting}
              data-test-signup-form-submit
            >
              Verify
            </Button>
          </Flex>
        </Form>
      </PanelSection>
    </Box>
  );
}
